import { Box } from '@mui/material'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import AgDataTable from '../ag-data-table/DataTable'
import { ColDef, GetRowIdParams, GridApi, IServerSideGetRowsParams } from '@ag-grid-community/core'
import { applicationService } from '../../lib/services'
import { DomainContext } from '../contexts/DomainContext'
import CompanySelector from '../manage-users/CompanySelector'
import DomainSelector from '../manage-users/DomainSelector'

const tableColumns: ColDef[] = [
    { headerName: 'ID', field: 'id', width: 90, hide: true },
    { headerName: 'Name', field: 'name', flex: 1, editable: false, filter: false },
    {
        headerName: 'Company',
        field: 'company',
        flex: 1,
        editable: params => false,
        filter: false,
        cellEditor: CompanySelector,
        cellEditorParams: params => {
            return {
                params,
            }
        },
        onCellValueChanged: params => {
            params.node.setDataValue('domain', '--')
            params.api.refreshCells({
                rowNodes: [params.node],
                columns: ['domain'],
            })
        },
    },
    {
        headerName: 'Domain',
        field: 'domain',
        flex: 1,
        editable: params => false,
        cellEditor: DomainSelector,
        cellEditorParams: params => {
            return {
                params,
            }
        },
        filter: false,
    },
]

const ManageApps = () => {
    const dataTableRef = useRef(null)
    const [gridAPI, setGridAPI] = useState<GridApi>(null)
    const [gridStatus, setGridStatus] = useState<string>('')
    const { companies, domains, loading } = useContext(DomainContext)
    const [changes, stageChanges] = useState<any[]>([])

    const getRowId = useCallback((params: GetRowIdParams) => params.data.id, [])

    useEffect(() => {
        if (gridAPI) {
            if (loading) {
                gridAPI?.showLoadingOverlay()
            } else {
                gridAPI?.hideOverlay()
                gridAPI?.setGridOption('serverSideDatasource', createDatasource())
            }
        }
    }, [loading, gridAPI])

    const addCompanyAndDomainNames = async response => {
        if (!response) return response
        const companiesList = companies ?? []
        const domainsList = domains ?? []

        const companyNames = companiesList?.reduce((acc, item) => {
            acc[item.id] = item?.name
            return acc
        }, {})

        const domainNames = domainsList?.reduce((acc, item) => {
            acc[item.id] = item?.name
            return acc
        }, {})
        response?.forEach(obj => {
            const companyName = companyNames[obj?.companyId]
            const domainName = domainNames[obj?.domainId]
            obj.company = companyName || '--'
            obj.domain = domainName || '--'
        })

        return response
    }
    const createDatasource = (searchText = null) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                try {
                    if (loading) return
                    let response = []
                    response = await applicationService.getApps()
                    // response = response?.filter(obj => obj.permission === 6)
                    response = await addCompanyAndDomainNames(response)

                    if (searchText !== null) {
                        response = response?.filter(app => app?.name?.toLowerCase().includes(searchText?.toLowerCase()))
                    }
                    if (loading) params.api.showLoadingOverlay()
                    else if (Number(response?.length) === 0) params.api.showNoRowsOverlay()
                    else params.api?.hideOverlay()

                    setGridStatus(`Showing 1 - ${response?.length} of ${response?.length} apps`)
                    params.success({ rowData: response ?? [] })
                } catch (error) {
                    console.error(error)
                    params.fail()
                }
            },
        }
    }
    const onGridReady = useCallback((params: { api: GridApi }) => {
        const { api } = params
        setGridAPI(api)
        api.setGridOption('serverSideDatasource', createDatasource())
    }, [])
    const handleSearch = value => {
        gridAPI.setGridOption('serverSideDatasource', createDatasource(value))
    }
    const handleChange = ({ oldValue, newValue, data, column, colDef }: any) => {
        if (newValue === oldValue || (newValue !== 0 && !newValue && !oldValue)) return
        const companyId =
            data.company && data.company !== '--' ? companies?.find(company => company.name === data.company)?.id : null
        const domainId =
            data.domain && data.domain !== '--' ? domains?.find(domain => domain.name === data.domain)?.id : null
        let updatedApp = { ...data }

        updatedApp = { ...updatedApp, companyId, domainId }

        delete updatedApp.company
        delete updatedApp.domain
        stageChanges({
            ...changes,
            [data.id]: updatedApp,
        })
    }

    return (
        <Box sx={{ height: '100%', p: 1 }}>
            <AgDataTable
                ref={dataTableRef}
                onGridReady={onGridReady}
                columns={tableColumns}
                extraActions={[]}
                title="Manage Apps"
                gridStatus={gridStatus}
                handleSearch={handleSearch}
                getRowId={getRowId}
                rowSelection="single"
                handleCellEdit={handleChange}
            />
        </Box>
    )
}

export default ManageApps
